import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GroupsIcon from '@mui/icons-material/Groups';
import GridOnIcon from '@mui/icons-material/GridOn';
import { ResponsiveLine } from '@nivo/line';
import { BRAND } from '../../theme/brandTheme';

const OUTCOME_COLORS = {
  Kazandı: '#70AD47',
  Kaybetti: '#C00000',
  Etkisiz: '#7F7F7F',
};

const lineTheme = {
  axis: {
    ticks: { text: { fill: '#666', fontSize: 11 } },
    legend: { text: { fill: '#666', fontSize: 12 } },
  },
  grid: { line: { stroke: '#eee' } },
  tooltip: { container: { fontSize: 12, borderRadius: 6 } },
};

// Cell background intensity by value vs column max
const heatCellSx = (value, max) => {
  if (!value || !max) return { bgcolor: 'action.hover', color: 'text.secondary' };
  const ratio = value / max;
  return {
    bgcolor: `rgba(36, 11, 75, ${0.08 + ratio * 0.55})`,
    color: ratio > 0.5 ? '#fff' : 'text.primary',
    fontWeight: value > 0 ? 600 : 400,
  };
};

/** Faz 3 — win rate trend, analyst leaderboard, metric heatmap */
const StatisticsAdvancedCharts = ({ charts }) => {
  const { winRateTrend = [], analystLeaderboard = [], metricHeatmap = [] } = charts || {};

  const lineData = useMemo(
    () => [
      {
        id: 'Win Rate',
        color: BRAND.green,
        data: winRateTrend.map((d) => ({
          x: d.month,
          y: d.winRate,
        })),
      },
    ],
    [winRateTrend],
  );

  const hasTrend = winRateTrend.some((d) => d.testsWithResult > 0);
  const heatMax = useMemo(() => {
    let max = 0;
    metricHeatmap.forEach((row) => {
      max = Math.max(max, row.Kazandı, row.Kaybetti, row.Etkisiz);
    });
    return max;
  }, [metricHeatmap]);

  if (!charts) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, mb: 2 }}>
        İleri Analitik
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <ShowChartIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                Win Rate Trendi
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Son 6 ay — sonuçlanan testlerde kazanma oranı (%)
              </Typography>
              {!hasTrend ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  Trend için sonuçlanmış test gerekli.
                </Typography>
              ) : (
                <Box sx={{ height: 260 }}>
                  <ResponsiveLine
                    data={lineData}
                    margin={{ top: 16, right: 24, bottom: 56, left: 48 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 100 }}
                    axisBottom={{ tickRotation: -30, legend: 'Ay', legendOffset: 44 }}
                    axisLeft={{ legend: 'Win rate %', legendOffset: -40 }}
                    colors={[BRAND.green]}
                    pointSize={8}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    useMesh
                    enableGridX={false}
                    theme={lineTheme}
                    tooltip={({ point }) => {
                      const row = winRateTrend.find((d) => d.month === point.data.x);
                      return (
                        <div style={{ padding: '8px 10px', background: '#fff', border: '1px solid #ccc' }}>
                          <strong>{point.data.xFormatted}</strong>
                          <br />
                          Win rate: %{point.data.yFormatted}
                          <br />
                          {row?.won}/{row?.testsWithResult} test
                        </div>
                      );
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <GroupsIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                Analist Performansı
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Sorumlu analiste göre test win rate
              </Typography>
              {!analystLeaderboard.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  Analist ataması yok.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Analist</TableCell>
                        <TableCell align="right">Test</TableCell>
                        <TableCell align="right">Win %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analystLeaderboard.map((row) => (
                        <TableRow key={row.analystId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{row.username}</TableCell>
                          <TableCell align="right">
                            {row.testsWithResult}/{row.testCount}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: row.winRate >= 50 ? BRAND.green : 'text.secondary',
                            }}
                          >
                            %{row.winRate}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            <GridOnIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
            Metrik Bazlı Sonuç Isı Haritası
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Her metrik satırı için Kazandı / Kaybetti / Etkisiz sayısı (metrik düzeyinde)
          </Typography>
          {!metricHeatmap.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Metrik sonucu olan rapor yok.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Metrik</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: OUTCOME_COLORS.Kazandı }}>
                      Kazandı
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: OUTCOME_COLORS.Kaybetti }}>
                      Kaybetti
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: OUTCOME_COLORS.Etkisiz }}>
                      Etkisiz
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Win %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metricHeatmap.map((row) => (
                    <TableRow key={row.metric} hover>
                      <TableCell sx={{ fontWeight: 600, maxWidth: 200 }}>
                        {row.metric}
                      </TableCell>
                      <TableCell align="center" sx={heatCellSx(row.Kazandı, heatMax)}>
                        {row.Kazandı}
                      </TableCell>
                      <TableCell align="center" sx={heatCellSx(row.Kaybetti, heatMax)}>
                        {row.Kaybetti}
                      </TableCell>
                      <TableCell align="center" sx={heatCellSx(row.Etkisiz, heatMax)}>
                        {row.Etkisiz}
                      </TableCell>
                      <TableCell align="right">%{row.winRate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StatisticsAdvancedCharts;
